<template>
  <!-- prettier-ignore -->
  <div
    class="box"
    :data-product-id="product.productId"
    :data-group-id="groupId"
    :data-category-id="categoryId"
  >
    <div class="main product">
      <the-typography
        class="product__punchline"
        type="heading04"
        v-text="$t(punchline)"
      />

      <div class="product__costs">
        <product-price
          class="product__price"
          :price="selectedPrice.price"
          :prefix="pricePrefix"
          :suffix="$t('product.currency')"
        />

        <div
          v-if="reduction.price > 0"
          class="product__reduction"
        >
          <div class="reduction-instead">
            {{ $t('product.insteadOf') }}
            <product-price :price="reduction.price" />
          </div>

          <div
            v-for="(r, index) in reduction.labels"
            :key="index"
            class="reduction__info"
            v-text="getReductionLabel(r, index)"
          />
        </div>
      </div>

      <the-typography
        class="product__name"
        type="heading03"
        v-text="$t(`content.products.${product.productId}.name`)"
      />

      <the-typography
        class="product__description"
        type="bodyLargeLong"
        v-html="$t(`content.products.${product.productId}.description`)"
      />

      <div class="product__content">
        <ul class="product__bullets">
          <li
            v-for="(bullet, key) in tm(`content.products.${product.productId}.bulletPoints`)"
            :key="key"
            v-text="bullet"
          />
        </ul>

        <the-message
          v-if="additionalInfo"
          class="product__message"
          is-html
          :text="additionalInfo"
        />
      </div>
      <div
        v-if="hasOptionsValue && !readonly"
        class="product__criterias"
      >
        <div
          v-if="productHasAccident"
          class="options-accident options"
        >
          <div class="options__title">
            <the-typography type="heading05">
              <span v-text="optionTitles.accident.title" />
              &nbsp;
              <the-icon
                v-tooltip="optionTitles.accident.tooltip"
                art="light"
                html-tooltip
                name="circle-info"
              />
            </the-typography>
          </div>
          <product-accident
            :option="selectedPrice"
            @select="selectOption"
          />
        </div>
        <div
          v-if="productHasFranchise"
          class="options-franchise options"
        >
          <div class="options__title">
            <the-typography type="heading05">
              <span v-text="optionTitles.franchise.title" />
              &nbsp;
              <the-icon
                v-if="optionTitles.franchise.title"
                v-tooltip="optionTitles.franchise.tooltip"
                art="light"
                html-tooltip
                name="circle-info"
              />
            </the-typography>
          </div>
          <product-franchise
            inline
            :options="product.prices"
            :source="source"
            @select="selectOption"
          />
          <transition name="the-transition">
            <the-message
              v-if="showFranchiseInfoMessage"
              class="options-franchise__message"
              severity="info"
              :title="$t('product.detail.franchiseInfo.title')"
              :text="$t('product.detail.franchiseInfo.text')"
            />
          </transition>
        </div>

        <div
          v-if="productHasDuration"
          class="options-duration options"
        >
          <div class="options__title">
            <the-typography type="heading05">
              <span v-text="optionTitles.duration.title" />
              &nbsp;
              <the-icon
                v-if="optionTitles.duration.tooltip"
                v-tooltip="optionTitles.duration.tooltip"
                art="light"
                html-tooltip
                name="circle-info"
              />
            </the-typography>
          </div>

          <product-duration
            :category-id="categoryId"
            :options="product.prices"
            :product-id="product.productId"
            :source="source"
            @select="selectOption"
          />
        </div>

        <div
          v-if="productHasVitalFranchise"
          class="options-franchise options"
        >
          <product-vital-franchise
            inline
            :options="product.prices"
            :source="source"
            @select="selectOption">
            <template #amValue>
              <div class="options__title">
                <the-typography type="heading05">
                  <span v-text="optionTitles.amValue.title" />
                  &nbsp;
                  <the-icon
                    v-if="optionTitles.amValue.title"
                    v-tooltip="optionTitles.amValue.tooltip"
                    art="light"
                    html-tooltip
                    name="circle-info"
                  />
                </the-typography>
              </div>
            </template>

            <template #gfValue>
              <div class="options__title">
                <the-typography type="heading05">
                  <span v-text="optionTitles.gfValue.title" />
                  &nbsp;
                  <the-icon
                    v-if="optionTitles.gfValue.title"
                    v-tooltip="optionTitles.gfValue.tooltip"
                    art="light"
                    html-tooltip
                    name="circle-info"
                  />
                </the-typography>
              </div>
            </template>
          </product-vital-franchise>
        </div>
      </div>

      <div
        v-if="product.doctor && !readonly"
        class="product__doctor"
      >
        <the-typography
          type="heading05"
          v-text="$t('product.doctor.titleShort')"
        />

        <product-doctor
          :person-id="personId"
          :product="product"
          :source="source"
          @search="searchMedicalOffice(product)"
        />
      </div>

      <div class="product__actions">
        <div class="product__links">
          <comparison-link
            v-if="te(`content.products.${product.productId}.benefitPath`)"
            type="benefit"
            :content="tm(`content.products.${product.productId}`)"
            :source="SOURCE.PRODUCT_BENEFITS"
            :product="product"
          >
            {{ $t('comparison.link') }}
          </comparison-link>
          <a
            v-else-if="te(`content.products.${product.productId}.linkToContentPage`)"
            :href="$t(`content.products.${product.productId}.linkToContentPage`)"
          >
            {{ $t('comparison.productPage') }}
          </a>

          <Form.Button
            v-if="showHospitalSearch"
            class="product__hospital-search"
            plain
            type="button"
            :text="$t('product.detail.hospitalSearch')"
            @click="showHospitalSearchModal = true"
          />

          <the-form-modal
            v-model="showHospitalSearchModal"
            name="hospitalSearchModal"
          >
            <iframe
              id="hospitalSearch"
              title="hospitalSearch"
              class="hospital-search__iframe"
              :src="hospitalSearchUrl"
              referrerpolicy="strict-origin"
              sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            ></iframe>
          </the-form-modal>
          <comparison-link
            v-if="te(`content.products.${product.productId}.comparisonPath`)"
            type="comparison"
            :content="tm(`content.products.${product.productId}`)"
            :source="SOURCE.PRODUCT_COMPARISON"
            :product="product"
          >
            {{ $t(`content.products.${product.productId}.comparisonLinkName`) }}
          </comparison-link>
        </div>

        <template v-if="!readonly && !isProductReadonly">
          <product-action
            v-if="!isProductNotDeletable"
            :person-id="personId"
            :product="product"
            :selected="product.selected"
            has-loading-indicator
            @add="addProduct(product.productId)"
            @remove="removeProduct(product.productId)"
            @search="searchMedicalOffice(product)"
          />
          <Form.Button
            v-else
            type="button"
            secondary
            disabled
            :text="$t('product.detail.cancellation.button')"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, computed, ref, onMounted } from 'vue'
import { events$, loading$ } from '@/services'

import ComparisonLink from '@/components/Comparison/ComparisonLink'
import ProductAction from '@/components/Product/ProductAction'
import ProductDoctor from '@/components/Product/ProductDoctor'
import ProductFranchise from '@/components/Product/ProductFranchise'
import ProductVitalFranchise from '@/components/Product/ProductVitalFranchise'
import ProductDuration from '@/components/Product/ProductDuration'
import ProductPrice from '@/components/Product/ProductPrice'
import ProductAccident from '@/components/Product/ProductAccident'
import * as Form from '@/components/Form'

import { CATEGORY_TYPE, PRODUCT_STATE } from '@/config/constants'
import { EVENT_MEDICAL_OFFICE_MODAL_OPEN, EVENT_PRODUCT, SOURCE } from '@/config/events'

import { useBasket, useI18n, usePerson, useProduct, useProductState, useStatus } from '@/hooks'

// Hooks
const { getCategoryIdFromProduct, isUpgrade } = useProduct()
const { getPerson } = usePerson()
const { state } = useProductState()
const { locale, t, te, tm } = useI18n()

// Init
const props = defineProps({
  categoryId: {
    type: String,
    required: true,
  },

  closable: {
    type: Boolean,
    default: false,
  },

  groupId: {
    type: String,
    required: true,
  },

  options: {
    type: Boolean,
    default: true,
  },

  personId: {
    type: String,
    required: true,
  },

  product: {
    type: Object,
    required: true,
  },

  readonly: {
    type: Boolean,
    default: false,
  },

  source: {
    type: String,
    default: SOURCE.CONFIGURATOR,
  },
})
const emit = defineEmits(['added', 'closed', 'removed', 'update'])

// Data
const showFranchiseInfoMessage = ref(false)
const showHospitalSearchModal = ref(false)
// Computed
const additionalInfo = computed(() => {
  const key = `content.products.${props.product.productId}`
  const { isUnborn } = usePerson()
  if (isUnborn(props.personId) && te(`${key}.additionalInfoUnborn`)) {
    return t(`${key}.additionalInfoUnborn`)
  } else if (te(`${key}.additionalInfo`)) {
    return t(`${key}.additionalInfo`)
  }
  return false
})

const productHasAccident = computed(() => {
  const { hasAccident } = useProduct()
  return hasAccident(props.product) && optionTitles.value.accident
})

const productHasDuration = computed(() => {
  const { hasDuration } = useProduct()
  return hasDuration(props.product) && optionTitles.value.duration
})

const productHasFranchise = computed(() => {
  const { hasFranchise } = useProduct()
  return hasFranchise(props.product) && optionTitles.value.franchise
})

const productHasVitalFranchise = computed(() => {
  const { hasVitalFranchise } = useProduct()
  return hasVitalFranchise(props.product)
})

const hasOptionsValue = computed(() => {
  const { hasOptions } = useProduct()
  return hasOptions(props.product) && props.options
})

const hospitalSearchUrl = computed(() => {
  return `${import.meta.env.VITE_SANITAS_PATH}/content/sanitas-internet/appintegration/${
    locale.value
  }/index/hdc-list-for-app.html`
})

const isProductNotDeletable = computed(() => {
  return productState.value === PRODUCT_STATE.NOT_DELETABLE
})

const isProductReadonly = computed(() => {
  return productState.value === PRODUCT_STATE.READONLY
})

const optionTitles = computed(() => {
  const { getOptionTitles } = useProduct()

  return getOptionTitles(props.product.productId, props.groupId, props.categoryId)
})

const selectedPrice = computed(() => {
  return props.product.prices.find(p => p.selected) || {}
})

const pricePrefix = computed(() => {
  return props.product?.showDiscountGroup && !props.product.selected ? t('product.asof') : ''
})

const productState = computed(() => {
  const partnerNumber = getPerson(props.personId).partnerNumber
  if (!partnerNumber) return PRODUCT_STATE.EDITABLE
  return state(partnerNumber, props.product.productId)
})

const punchline = computed(() => {
  const group = `content.groups.${props.groupId}.title`
  const product = `content.products.${props.product.productId}.punchline`

  return te(product) ? product : group
})

const reduction = computed(() => {
  const amount = Math.abs(selectedPrice.value.reduction)
  return {
    price: amount > 0 ? selectedPrice.value.price + amount : 0,
    labels: selectedPrice.value?.reductions ?? [],
  }
})

const showHospitalSearch = computed(() => {
  return props.product.productId === 'SAMB05Produkt'
})

// Methods
function addProduct(productId) {
  const mode = isUpgrade(productId) ? 'ADD' : 'REPLACE'

  emit('added', {
    productId,
    personId: props.personId,
    categoryId: props.categoryId,
    mode,
    forceAdd: true,
  })
}

function getReductionLabel(__reduction, i) {
  const type = t(`info.${__reduction.type}`)
  const amount = Math.abs(__reduction.amount).toFixed(2)
  const currency = t('product.currency')
  const separator = reduction.value.labels.length - 1 !== i ? ';' : ''

  return `${type} ${amount} ${currency}${separator}`
}

function isFranchiseInfoMessageShown(productId, selectedOption) {
  const option = selectedOption || props.product.prices.find(p => p.selected === true)
  const categoryId = getCategoryIdFromProduct(productId)

  if (!option) return

  return categoryId === CATEGORY_TYPE.HOSP && option.value && option.value !== 0
}

async function removeProduct(productId) {
  const { askChangeConfirmation } = useStatus()
  const { removeProductFromOvpBasket } = useBasket()

  if (!(await askChangeConfirmation(t('product.confirmRemoval')))) {
    return
  }

  loading$.start({ blocking: false })
  const __basket = await removeProductFromOvpBasket(props.personId, productId)
  events$.emit(EVENT_PRODUCT.REMOVED, {
    basket: __basket,
    categoryId: props.categoryId,
    personId: props.personId,
    productId: props.product.productId,
    source: props.source,
  })
  emit('removed', productId)
  loading$.end()
}

function searchMedicalOffice(product) {
  emit('closed')

  events$.emit(EVENT_MEDICAL_OFFICE_MODAL_OPEN, {
    source: props.source,
    personId: props.personId,
    product,
  })
}

async function selectOption(option, simulate = false) {
  loading$.start()

  const { updateOvpBasket, persistBasket } = useBasket()
  const { selectProductOption } = useProduct()

  if (!props.product.selected) {
    simulate = true
  }

  try {
    let __basket = await selectProductOption(props.personId, props.product.productId, option)
    __basket = await updateOvpBasket(__basket, { simulate })
    __basket = await persistBasket(__basket)

    events$.emit(EVENT_PRODUCT.CHANGED, {
      basket: __basket,
      categoryId: props.categoryId,
      option,
      personId: props.personId,
      productId: props.product.productId,
      source: props.source,
    })

    showFranchiseInfoMessage.value = isFranchiseInfoMessageShown(props.product.productId, option)
    emit('update', props.product.productId)
    loading$.end()
    return __basket
  } catch (_e) {
    loading$.failed()
  }
}

// Lifecycle Hooks
onMounted(() => {
  showFranchiseInfoMessage.value = isFranchiseInfoMessageShown(props.product.productId)
})
onBeforeUnmount(() => {
  emit('closed')
})
</script>

<style name="mobile" scoped>
.box {
  cursor: default;
  display: grid;
  grid-template-rows: 1fr minmax(0, auto);

  .product__costs {
    display: flex;
    flex-direction: column;

    .product__price {
      font-size: 50px;
      line-height: 60px;

      & :deep(.price__unavailable) {
        font-size: 30px;
      }
    }

    .product__reduction {
      font-size: 14px;
    }
  }

  .product__name {
    margin: 16px 0 0 0;
  }

  .product__description {
    margin: 8px 0 0 0;
  }

  .product__content {
    .product__bullets {
      margin: 8px 0 0 0;
      padding: 0 0 0 20px;
    }

    .product__message {
      margin: 24px 0 0 0;
    }
  }

  .product__criterias {
    margin: 16px 0 0 0;

    .options {
      margin: 8px 0 0 0;
    }
  }

  .product__doctor {
    margin: 16px 0 0 0;
  }

  .product__actions {
    margin: 16px 0 0 0;
    display: flex;
    flex-direction: column;
    row-gap: 16px;

    .product__links {
      display: flex;
      flex-direction: column;
      line-height: 50px;
    }
  }
}

.options-franchise__message {
  margin-top: 16px;
}

.button.product__hospital-search {
  color: var(--c-primary-color-3);
  width: 100%;
  min-height: 50px;
  justify-content: flex-start;
  padding: 0;
}

.hospital-search__iframe {
  display: flex;
  height: 100vh;
  width: 100%;
  overflow: hidden;
  border: none;
  padding: calc(var(--modal-offset-inner) + 24px) var(--modal-offset-outer) var(--modal-offset-inner);
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .box {
    .product__costs {
      flex-direction: row;
      gap: 24px;

      .product__reduction {
        padding: 16px 0 0 0;
      }
    }

    .product__actions {
      margin: 44px 0 0;
      flex-direction: row;
      justify-content: space-between;

      .product__links {
        flex-direction: row;
        gap: 48px;
      }
    }
  }
}
</style>
